import {
  BUILT_IN_FUND_TYPES,
  decodeFundTypeValue,
} from '@/components/shared/fund-type-select';

export { parseCsv } from '@/pages/people/member-import-csv';

export type FundImportField =
  | 'name'
  | 'code'
  | 'fundType'
  | 'customTypeName'
  | 'defaultCurrency'
  | 'description'
  | 'hideGiverIdentity'
  | 'acceptsPledges'
  | 'availableForPrograms'
  | 'isActive';

export const FUND_IMPORT_FIELDS: FundImportField[] = [
  'name',
  'code',
  'fundType',
  'customTypeName',
  'defaultCurrency',
  'description',
  'hideGiverIdentity',
  'acceptsPledges',
  'availableForPrograms',
  'isActive',
];

export const REQUIRED_FUND_IMPORT_FIELDS: FundImportField[] = ['name', 'code'];

export const FUND_IMPORT_FIELD_LABEL_KEYS: Record<FundImportField, string> = {
  name: 'funds.name',
  code: 'funds.code',
  fundType: 'funds.type',
  customTypeName: 'funds.customType.name',
  defaultCurrency: 'funds.currency',
  description: 'funds.import.fields.description',
  hideGiverIdentity: 'funds.privacy',
  acceptsPledges: 'funds.acceptsPledges',
  availableForPrograms: 'funds.availableForPrograms',
  isActive: 'funds.active',
};

export type FundImportRowIssue =
  | 'missingName'
  | 'missingCode'
  | 'invalidCurrency'
  | 'invalidBoolean'
  | 'invalidFundType'
  | 'missingCustomType'
  | 'duplicateCodeInFile'
  | 'duplicateCodeExisting';

export interface FundCustomTypeOption {
  id: string;
  name: string;
  code: string;
}

export interface FundImportRowPayload {
  name: string;
  code: string;
  fundType?: string;
  customTypeId?: string | null;
  customTypeName?: string;
  defaultCurrency?: string;
  description?: string | null;
  recordGiverIdentity?: boolean;
  acceptsPledges?: boolean;
  availableForPrograms?: boolean;
  isActive?: boolean;
}

export interface PreparedFundImportRow {
  index: number;
  source: Record<string, string>;
  payload?: FundImportRowPayload;
  issues: FundImportRowIssue[];
}

export const MAX_FUND_IMPORT_ROWS = 500;

export const SAMPLE_FUND_CSV = `name,code,type,currency
Thanksgiving,THANKSGIVING,offering,GHS
Building fund,BUILDING,Building Projects,GHS
`;

const HEADER_ALIASES: Record<FundImportField, string[]> = {
  name: ['name', 'fund name', 'fundname', 'fund_name'],
  code: ['code', 'fund code', 'fundcode', 'fund_code'],
  fundType: ['type', 'fund type', 'fundtype', 'fund_type', 'category'],
  customTypeName: ['custom type', 'customtype', 'custom_type', 'type name', 'typename'],
  defaultCurrency: ['currency', 'default currency', 'defaultcurrency', 'default_currency'],
  description: ['description', 'notes', 'note'],
  hideGiverIdentity: [
    'hide giver',
    'hide giver identity',
    'hide giver names',
    'hide identity',
    'privacy',
    'hidegiveridentity',
    'hide_giver_identity',
  ],
  acceptsPledges: ['accepts pledges', 'acceptspledges', 'accepts_pledges', 'pledges'],
  availableForPrograms: [
    'available for programs',
    'availableforprograms',
    'available_for_programs',
    'registration',
    'event registration',
  ],
  isActive: ['active', 'is active', 'isactive', 'is_active', 'status'],
};

const normalizeHeader = (value: string): string =>
  value.replace(/^\uFEFF/, '').trim().toLowerCase().replace(/\s+/g, ' ');

export const guessFundFieldForHeader = (header: string): FundImportField | null => {
  const key = normalizeHeader(header);
  if (!key) return null;
  for (const field of FUND_IMPORT_FIELDS) {
    if (HEADER_ALIASES[field].includes(key) || key === field.toLowerCase()) return field;
  }
  return null;
};

export const buildFundImportMapping = (
  headers: string[],
): Record<string, FundImportField | ''> => {
  const mapping: Record<string, FundImportField | ''> = {};
  const used = new Set<FundImportField>();
  for (const header of headers) {
    const guess = guessFundFieldForHeader(header);
    if (guess && !used.has(guess)) {
      mapping[header] = guess;
      used.add(guess);
    } else {
      mapping[header] = '';
    }
  }
  return mapping;
};

const parseBoolean = (value: string): boolean | null => {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return null;
  if (['yes', 'y', 'true', '1', 'active'].includes(normalized)) return true;
  if (['no', 'n', 'false', '0', 'inactive'].includes(normalized)) return false;
  return null;
};

const normalizeCurrency = (value: string): string | null => {
  const code = value.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : null;
};

const normalizeFundTypeInput = (
  raw: string,
  customTypes: FundCustomTypeOption[],
  fundTypeLabels: Record<string, string>,
): { fundType: string; customTypeId: string | null; customTypeName?: string } | null => {
  const normalized = raw.trim().toLowerCase();
  if (!normalized) return null;

  for (const type of BUILT_IN_FUND_TYPES) {
    if (normalized === type || normalized === type.replace(/_/g, ' ')) {
      return { fundType: type, customTypeId: null };
    }
    const label = fundTypeLabels[type]?.trim().toLowerCase();
    if (label && normalized === label) {
      return { fundType: type, customTypeId: null };
    }
  }

  if (['custom', 'other'].includes(normalized)) {
    return { fundType: 'custom', customTypeId: null };
  }

  const customMatch = customTypes.find(
    (row) =>
      row.name.trim().toLowerCase() === normalized ||
      row.code.trim().toLowerCase() === normalized,
  );
  if (customMatch) {
    return { fundType: 'custom', customTypeId: customMatch.id };
  }

  return { fundType: 'custom', customTypeId: null, customTypeName: raw.trim() };
};

export const prepareFundImportRows = (input: {
  rows: Record<string, string>[];
  mapping: Record<string, FundImportField | ''>;
  customTypes: FundCustomTypeOption[];
  fundTypeLabels: Record<string, string>;
  existingCodes: Set<string>;
  defaultCurrency: string;
}): PreparedFundImportRow[] => {
  const seenCodes = new Map<string, number>();

  return input.rows.map((source, index) => {
    const issues: FundImportRowIssue[] = [];
    const values: Partial<Record<FundImportField, string>> = {};

    for (const [header, field] of Object.entries(input.mapping)) {
      if (!field) continue;
      values[field] = source[header]?.trim() ?? '';
    }

    const name = values.name?.trim() ?? '';
    const code = values.code?.trim() ?? '';
    if (!name) issues.push('missingName');
    if (!code) issues.push('missingCode');

    const normalizedCode = code.toUpperCase();
    if (code) {
      if (seenCodes.has(normalizedCode)) {
        issues.push('duplicateCodeInFile');
      } else {
        seenCodes.set(normalizedCode, index);
      }
      if (input.existingCodes.has(normalizedCode)) {
        issues.push('duplicateCodeExisting');
      }
    }

    let defaultCurrency = input.defaultCurrency;
    if (values.defaultCurrency) {
      const parsedCurrency = normalizeCurrency(values.defaultCurrency);
      if (!parsedCurrency) {
        issues.push('invalidCurrency');
      } else {
        defaultCurrency = parsedCurrency;
      }
    }

    let fundType = 'offering';
    let customTypeId: string | null = null;
    let customTypeName: string | undefined;

    const typeInput = values.fundType?.trim() ?? '';
    const customTypeInput = values.customTypeName?.trim() ?? '';

    if (typeInput) {
      const parsedType = normalizeFundTypeInput(typeInput, input.customTypes, input.fundTypeLabels);
      if (!parsedType) {
        issues.push('invalidFundType');
      } else {
        fundType = parsedType.fundType;
        customTypeId = parsedType.customTypeId;
        customTypeName = parsedType.customTypeName;
      }
    }

    if (customTypeInput) {
      const parsedCustom = normalizeFundTypeInput(
        customTypeInput,
        input.customTypes,
        input.fundTypeLabels,
      );
      if (parsedCustom?.fundType === 'custom') {
        fundType = 'custom';
        customTypeId = parsedCustom.customTypeId;
        customTypeName = parsedCustom.customTypeName ?? customTypeInput;
      }
    }

    if (fundType === 'custom' && !customTypeId && !customTypeName) {
      issues.push('missingCustomType');
    }

    const hideGiverIdentity = values.hideGiverIdentity
      ? parseBoolean(values.hideGiverIdentity)
      : null;
    if (values.hideGiverIdentity && hideGiverIdentity === null) {
      issues.push('invalidBoolean');
    }

    const acceptsPledges = values.acceptsPledges ? parseBoolean(values.acceptsPledges) : null;
    if (values.acceptsPledges && acceptsPledges === null) {
      issues.push('invalidBoolean');
    }

    const availableForPrograms = values.availableForPrograms
      ? parseBoolean(values.availableForPrograms)
      : null;
    if (values.availableForPrograms && availableForPrograms === null) {
      issues.push('invalidBoolean');
    }

    let isActive: boolean | undefined;
    if (values.isActive) {
      const parsedActive = parseBoolean(values.isActive);
      if (parsedActive === null) {
        issues.push('invalidBoolean');
      } else {
        isActive = parsedActive;
      }
    }

    if (issues.length > 0) {
      return { index, source, issues };
    }

    const decoded = decodeFundTypeValue(
      customTypeId ? `custom:${customTypeId}` : fundType,
    );

    const payload: FundImportRowPayload = {
      name,
      code: normalizedCode,
      fundType: decoded.fundType,
      customTypeId: decoded.customTypeId,
      ...(customTypeName ? { customTypeName } : {}),
      defaultCurrency,
      description: values.description?.trim() ? values.description.trim() : undefined,
      ...(hideGiverIdentity != null ? { recordGiverIdentity: !hideGiverIdentity } : {}),
      ...(acceptsPledges != null ? { acceptsPledges } : {}),
      ...(availableForPrograms != null ? { availableForPrograms } : {}),
      ...(isActive != null ? { isActive } : {}),
    };

    return { index, source, payload, issues };
  });
};
